"use client";

import type { CoverSlideData, ClosingSlideData } from "@/lib/carousel/types";
import { FONT_PAIRINGS, type FontPairing } from "@/lib/carousel/fonts";

/**
 * Field-label → CoverSlideData-field convention: "Location" -> headlineTitle,
 * "Date" -> kickerText. The four stats[] slots match what lib/carousel/tripDraft.ts
 * (trip) and CarouselHomeClient.tsx (public import) already populate — 📍 location,
 * 🕐 duration, 🐦 species count, 📅 month — do not reorder without updating both.
 */
const STAT_SLOTS = [
  { index: 0, icon: "📍", label: "Location" },
  { index: 1, icon: "🕐", label: "Duration" },
  { index: 2, icon: "🐦", label: "Species" },
  { index: 3, icon: "📅", label: "Month" },
] as const;

const BG_SWATCHES = [
  { mode: "auto" as const, label: "Default", swatchClass: "car-bg-swatch--auto" },
  { mode: "light" as const, label: "White", swatchClass: "car-bg-swatch--light" },
  { mode: "dark" as const, label: "Black", swatchClass: "car-bg-swatch--dark" },
  { mode: "accent" as const, label: "Green", swatchClass: "car-bg-swatch--accent" },
  { mode: "transparent" as const, label: "Transparent", swatchClass: "car-bg-swatch--transparent" },
];

export interface ActiveSpeciesText {
  code?: string;
  namePrimary: string;
  nameSecondary: string;
  nameTertiary: string;
}

export interface EditorBottomPanelProps {
  activeKind: "cover" | "species" | "closing";

  // Photo controls — generic across cover/species/closing
  photoUrl: string | null;
  photoOffsetY: number;
  photoZoom: number;
  onPhotoOffsetYChange: (v: number) => void;
  onPhotoZoomChange: (v: number) => void;
  onChangePhotoClick: () => void;
  onRemovePhotoClick: () => void;

  // Fonts — global to the whole carousel, shown regardless of active slide
  pairingId: string;
  setPairingId: (id: string) => void;
  premiumLocked: boolean;
  onPremiumSwatchClick: () => void;

  // Cover text + background
  cover: CoverSlideData;
  updateCover: (patch: Partial<CoverSlideData>) => void;

  // Species text
  activeSpecies: ActiveSpeciesText | null;
  updateActiveSpecies: (patch: Partial<Omit<ActiveSpeciesText, "code">>) => void;

  // Closing text
  closing: ClosingSlideData;
  updateClosing: (patch: Partial<ClosingSlideData>) => void;
}

/** Everything for the active slide — photo controls, text fields, background —
 * shown at once, no mode-switching required (click a slide, edit anything).
 * Removing a (custom species / closing) slide is a button on the stage card
 * itself now, not here — see EditorStage.tsx. */
export default function EditorBottomPanel({
  activeKind,
  photoUrl, photoOffsetY, photoZoom, onPhotoOffsetYChange, onPhotoZoomChange, onChangePhotoClick, onRemovePhotoClick,
  pairingId, setPairingId, premiumLocked, onPremiumSwatchClick,
  cover, updateCover,
  activeSpecies, updateActiveSpecies,
  closing, updateClosing,
}: EditorBottomPanelProps) {
  function updateStat(index: number, value: string) {
    const stats = [...cover.stats];
    while (stats.length <= index) stats.push({ icon: STAT_SLOTS[stats.length].icon, value: "" });
    stats[index] = { icon: STAT_SLOTS[index].icon, value };
    updateCover({ stats });
  }
  const statValue = (index: number) => cover.stats[index]?.value ?? "";

  return (
    <div className="ced-bp-merged">
      <div className="ced-bp-inner ced-bp-photo-row">
        <div className="ced-bp-group">
          <span className="ced-bp-label">Zoom</span>
          <div className="ced-slider-row">
            <input type="range" min={100} max={200} step={2}
              value={Math.round(photoZoom * 100)}
              onChange={(e) => onPhotoZoomChange(parseInt(e.target.value, 10) / 100)} />
            <span className="ced-slider-value">{Math.round(photoZoom * 100)}%</span>
          </div>
        </div>
        <div className="ced-bp-group">
          <span className="ced-bp-label">Position ↑↓</span>
          <div className="ced-slider-row">
            <input type="range" min={-100} max={100} step={5}
              value={Math.round(photoOffsetY * 100)}
              onChange={(e) => onPhotoOffsetYChange(parseInt(e.target.value, 10) / 100)} />
            <span className="ced-slider-value">{Math.round(photoOffsetY * 100)}</span>
          </div>
        </div>
        <div className="ced-bp-group">
          <span className="ced-bp-label">Photo</span>
          <div className="ced-bp-btn-row">
            <button type="button" className="car-photo-btn" onClick={onChangePhotoClick}>
              {photoUrl ? "Change" : "Upload"}
            </button>
            {photoUrl && (
              <button type="button" className="car-photo-remove" onClick={onRemovePhotoClick} aria-label="Remove photo">✕</button>
            )}
          </div>
        </div>
        {activeKind === "cover" && (
          <div className="ced-bp-group">
            <span className="ced-bp-label">Card background</span>
            <div className="car-bg-swatch-row">
              {BG_SWATCHES.map((sw) => (
                <button key={sw.mode} type="button"
                  className={`car-bg-swatch ${sw.swatchClass}${(cover.backgroundMode ?? "auto") === sw.mode ? " car-bg-swatch--selected" : ""}`}
                  onClick={() => updateCover({ backgroundMode: sw.mode })}
                  aria-label={sw.label} title={sw.label} />
              ))}
            </div>
          </div>
        )}
        <div className="ced-bp-group">
          <span className="ced-bp-label">Fonts</span>
          <div className="car-picker-row">
            {FONT_PAIRINGS.map((p: FontPairing) => (
              <button
                key={p.id} type="button"
                className={`car-pill${p.id === pairingId ? " car-pill-on" : ""}`}
                onClick={() => {
                  if (p.premium && premiumLocked) { onPremiumSwatchClick(); return; }
                  setPairingId(p.id);
                }}
              >
                {p.name}{p.premium && <span className="car-pill-premium">★</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ced-bp-divider" />

      {activeKind === "cover" && (
        <div className="ced-bp-inner--grid">
          <div>
            <div className="car-label-row">
              <label className="car-label">Location (headline)</label>
              <input type="range" min={40} max={180} step={2}
                value={cover.headlineFontSize}
                onChange={(e) => updateCover({ headlineFontSize: parseInt(e.target.value, 10) })}
                className="car-mini-range" title={`Headline size: ${cover.headlineFontSize}px`} />
            </div>
            <div className="car-input-row">
              <input type="text" className="car-headline-input" value={cover.headlineTitle}
                onChange={(e) => updateCover({ headlineTitle: e.target.value })} placeholder="e.g. Big Almaty Lake" />
            </div>
          </div>
          <div>
            <div className="car-label-row">
              <label className="car-label">Subtitle</label>
              <input type="range" min={60} max={160} step={5}
                value={Math.round((cover.subtitleScale ?? 1) * 100)}
                onChange={(e) => updateCover({ subtitleScale: parseInt(e.target.value, 10) / 100 })}
                className="car-mini-range" title={`Subtitle size: ${Math.round((cover.subtitleScale ?? 1) * 100)}%`} />
            </div>
            <div className="car-input-row">
              <input type="text" className="car-headline-input" value={cover.subtitle}
                onChange={(e) => updateCover({ subtitle: e.target.value })} />
              {cover.subtitle !== "" && (
                <button type="button" className="car-clear-btn" aria-label="Remove subtitle"
                  onClick={() => updateCover({ subtitle: "" })}>✕</button>
              )}
            </div>
          </div>
          <div>
            <div className="car-label-row">
              <label className="car-label">Date</label>
              <input type="range" min={60} max={160} step={5}
                value={Math.round((cover.kickerScale ?? 1) * 100)}
                onChange={(e) => updateCover({ kickerScale: parseInt(e.target.value, 10) / 100 })}
                className="car-mini-range" title={`Top label size: ${Math.round((cover.kickerScale ?? 1) * 100)}%`} />
            </div>
            <div className="car-input-row">
              <input type="text" className="car-headline-input" value={cover.kickerText}
                onChange={(e) => updateCover({ kickerText: e.target.value })} />
              {cover.kickerText !== "" && (
                <button type="button" className="car-clear-btn" aria-label="Remove top label"
                  onClick={() => updateCover({ kickerText: "" })}>✕</button>
              )}
            </div>
          </div>
          {STAT_SLOTS.map((slot) => (
            <div key={slot.index}>
              <label className="car-label">{slot.icon} {slot.label}</label>
              <div className="car-input-row">
                <input type="text" className="car-headline-input" value={statValue(slot.index)}
                  onChange={(e) => updateStat(slot.index, e.target.value)} />
                {statValue(slot.index) !== "" && (
                  <button type="button" className="car-clear-btn" aria-label={`Remove ${slot.label}`}
                    onClick={() => updateStat(slot.index, "")}>✕</button>
                )}
              </div>
            </div>
          ))}
          <div>
            <div className="car-label-row">
              <label className="car-label">Stats size</label>
              <input type="range" min={60} max={160} step={5}
                value={Math.round((cover.statsScale ?? 1) * 100)}
                onChange={(e) => updateCover({ statsScale: parseInt(e.target.value, 10) / 100 })}
                className="car-mini-range" title={`Stats size: ${Math.round((cover.statsScale ?? 1) * 100)}%`} />
            </div>
          </div>
          <div>
            <label className="car-label">Swipe cue</label>
            <div className="car-input-row">
              <input type="text" className="car-headline-input" value={cover.swipeText ?? ""}
                onChange={(e) => updateCover({ swipeText: e.target.value })} placeholder="СВАЙП" />
              {(cover.swipeText ?? "") !== "" && (
                <button type="button" className="car-clear-btn" aria-label="Reset swipe cue"
                  onClick={() => updateCover({ swipeText: "" })}>✕</button>
              )}
            </div>
          </div>
        </div>
      )}

      {activeKind === "species" && activeSpecies && (
        <div className="ced-bp-inner--grid">
          <div>
            <label className="car-label">Headline name (shown largest)</label>
            <input className="car-headline-input" value={activeSpecies.namePrimary}
              onChange={(e) => updateActiveSpecies({ namePrimary: e.target.value })} />
          </div>
          <div>
            <label className="car-label">Scientific name (optional)</label>
            <input className="car-headline-input" value={activeSpecies.nameSecondary}
              onChange={(e) => updateActiveSpecies({ nameSecondary: e.target.value })} />
          </div>
          <div>
            <label className="car-label">Small caption (optional — e.g. English, under a regional headline)</label>
            <input className="car-headline-input" value={activeSpecies.nameTertiary}
              onChange={(e) => updateActiveSpecies({ nameTertiary: e.target.value })} />
          </div>
        </div>
      )}

      {activeKind === "closing" && (
        <div className="ced-bp-inner--grid">
          <div>
            <label className="car-label">Heading</label>
            <input className="car-headline-input" placeholder="See you in the field"
              value={closing.heading} onChange={(e) => updateClosing({ heading: e.target.value })} />
          </div>
          <div>
            <label className="car-label">Subtext</label>
            <input className="car-headline-input" placeholder="follow for more field diaries"
              value={closing.subtext} onChange={(e) => updateClosing({ subtext: e.target.value })} />
          </div>
          <div>
            <label className="car-label">Handle</label>
            <input className="car-headline-input" placeholder="@yourhandle"
              value={closing.handle} onChange={(e) => updateClosing({ handle: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
}
